/**
 * Bundle PDP — client-side orchestration.
 *
 * Three cooperating custom elements:
 *   <bundle-item-picker>   per bundle component (shoe / underscrub / sock); owns selection state + swatch UI
 *   <bundle-price-summary> listens to picker changes; renders aggregated price + compare-at strikethrough
 *   <bundle-add-to-cart>   listens to picker changes; toggles disabled state + submits multi-variant /cart/add.js
 *
 * Pickers cross-communicate via document-level events. Each picker reads its own catalog JSON from the
 * <script type="application/json" data-bundle-catalog> emitted by the bundle-item-picker Liquid snippet.
 */
(() => {
  'use strict';

  const EVT_CHANGE = 'bundle:picker-change';
  const EVT_READY = 'bundle:ready';
  const EVT_INCOMPLETE = 'bundle:incomplete';
  const EVT_ADDED = 'bundle:added';

  const registry = new Map();

  function getConfig() {
    const el = document.getElementById('bundle-config');
    if (!el) return { rememberSelection: false };
    try { return JSON.parse(el.textContent); } catch (e) { return { rememberSelection: false }; }
  }

  const config = getConfig();

  function formatMoney(cents) {
    // Whole-dollar amounts render without trailing zeros ($193 not $193.00); fractional amounts
    // keep two decimals ($193.50). Strip `.00` from any Shopify-formatted output too so the rule
    // is consistent regardless of which path produced the string.
    let formatted;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      formatted = window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    } else {
      const amount = cents / 100;
      const amountStr = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
      const code = window.Shopify?.currency?.active || 'USD';
      formatted = code === 'USD' ? `$${amountStr}` : `${amountStr} ${code}`;
    }
    return formatted.replace(/\.00(?=\D|$)/g, '');
  }

  function escapeHTML(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Yotpo refresh — debounced. Calls initWidgets() globally because Yotpo doesn't expose a
  // per-widget refresh API; widgets whose data-yotpo-product-id hasn't changed re-render to
  // identical content (no visible change). The loader spinner is only shown on pickers that
  // requested a refresh, so the shopper visually only sees masking on the swatch they clicked.
  let _yotpoTimer = null;
  let _yotpoUnmaskTimer = null;
  const _yotpoMasked = new Set();

  function scheduleYotpoRefresh(ratingEl) {
    if (!window.yotpoWidgetsContainer || typeof window.yotpoWidgetsContainer.initWidgets !== 'function') return;

    // Mask EVERY rating widget on the page with the white overlay — Yotpo's global initWidgets
    // will re-render all of them, and even widgets whose product hasn't changed flash briefly
    // mid-render. The spinner is only added to the picker that actually requested the refresh,
    // so the shopper sees one loader (visual cue) while the others are silently masked.
    document.querySelectorAll('[data-slot="preview-rating"]').forEach(el => {
      el.classList.add('is-refreshing');
      _yotpoMasked.add(el);
    });
    if (ratingEl) ratingEl.classList.add('is-target');

    if (_yotpoTimer) clearTimeout(_yotpoTimer);
    if (_yotpoUnmaskTimer) clearTimeout(_yotpoUnmaskTimer);

    _yotpoTimer = setTimeout(() => {
      _yotpoTimer = null;
      try { window.yotpoWidgetsContainer.initWidgets(); } catch (_) {}
      _yotpoUnmaskTimer = setTimeout(() => {
        _yotpoUnmaskTimer = null;
        _yotpoMasked.forEach(el => {
          el.classList.remove('is-refreshing');
          el.classList.remove('is-target');
        });
        _yotpoMasked.clear();
      }, 700);
    }, 350);
  }

  function buildDetail() {
    const entries = Array.from(registry.entries());
    const pickers = entries.map(([role, v]) => ({ role, ...v.state }));
    const anySelected = pickers.some(p => p.color);
    const allReady = pickers.length > 0 && pickers.every(p => p.variant_id);
    return {
      pickers,
      totalPrice: pickers.reduce((s, p) => s + (p.price || 0), 0),
      totalCompare: pickers.reduce((s, p) => s + (p.compare_at_price || p.price || 0), 0),
      anySelected,
      allReady,
    };
  }

  function notifyState() {
    const detail = buildDetail();
    document.dispatchEvent(new CustomEvent(EVT_CHANGE, { detail }));
    document.dispatchEvent(new CustomEvent(detail.allReady ? EVT_READY : EVT_INCOMPLETE, { detail }));
  }

  function sessionKey(role) {
    return `bundle:${window.location.pathname}:${role}`;
  }

  function persistSession(role, state) {
    if (!config.rememberSelection) return;
    try { sessionStorage.setItem(sessionKey(role), JSON.stringify(state)); } catch (_) {}
  }

  function loadSession(role) {
    if (!config.rememberSelection) return null;
    try { return JSON.parse(sessionStorage.getItem(sessionKey(role)) || 'null'); } catch (_) { return null; }
  }

  // =========================================================================
  // <bundle-item-picker>
  // =========================================================================
  class BundleItemPicker extends HTMLElement {
    connectedCallback() {
      this.role = this.dataset.role;
      this.hasGenderToggle = this.dataset.genderToggle === 'true';
      this.titleTemplate = this.dataset.titleTemplate || '{style}';
      this.catalog = this._loadCatalog();

      const defaultChip = this.querySelector('[data-slot="style-chips"] .bundle-chip:not([hidden])');
      const defaultStyle = defaultChip?.dataset.style || null;
      const defaultStyleLabel = defaultChip?.textContent.trim() || '';
      this.state = {
        gender: this.hasGenderToggle ? 'womens' : null,
        style_tag: defaultStyle,
        style_label: defaultStyleLabel,
        color: null,
        size: null,
        variant_id: null,
        price: null,
        compare_at_price: null,
        image: null,
        product_title: null,
        product_url: null,
      };

      const saved = loadSession(this.role);
      if (saved && typeof saved === 'object') {
        Object.assign(this.state, saved);
      }

      if (this._ensureValidStyle()) {
        this.state.color = null;
        this.state.size = null;
      }

      registry.set(this.role, { state: this.state, catalog: this.catalog });

      // On first render auto-select the first available color of the default style — matches legacy
      // behaviour where the first swatch is preselected when the page loads. Skip if a saved
      // session already restored a color choice.
      if (!this.state.color) {
        const firstAvailable = this._filtered().find(v => v.available) || this._filtered()[0];
        if (firstAvailable) {
          this.state.color = firstAvailable.color;
        }
      }
      this._resolveVariant();

      this._bindEvents();
      this._render();

      notifyState();
    }

    disconnectedCallback() {
      registry.delete(this.role);
      notifyState();
    }

    _loadCatalog() {
      const s = this.querySelector('script[data-bundle-catalog]');
      if (!s) return [];
      try { return JSON.parse(s.textContent) || []; } catch (_) { return []; }
    }

    _bindEvents() {
      const bind = (selector, datasetKey, cb) => {
        const container = this.querySelector(selector);
        if (!container) return;
        container.addEventListener('click', (e) => {
          const btn = e.target.closest(`[data-${datasetKey}]`);
          if (!btn || btn.disabled || btn.hidden) return;
          cb(btn.dataset[datasetKey]);
        });
      };
      // Mask Yotpo widgets synchronously the moment a swatch is clicked — the white overlay
      // goes on every rating area (so other widgets don't flash during global Yotpo init) and
      // the `is-target` spinner badge goes on this picker (visual feedback for the click).
      const maskAndSelect = (selectFn) => (v) => {
        document.querySelectorAll('[data-slot="preview-rating"]').forEach(el => el.classList.add('is-refreshing'));
        this.querySelector('[data-slot="preview-rating"]')?.classList.add('is-target');
        selectFn(v);
      };
      bind('[data-slot="gender-toggle"]', 'gender', maskAndSelect((v) => this._selectGender(v)));
      bind('[data-slot="style-chips"]', 'style', maskAndSelect((v) => this._selectStyle(v)));
      bind('[data-slot="color-swatches"]', 'color', maskAndSelect((v) => this._selectColor(v)));
      bind('[data-slot="size-swatches"]', 'size', maskAndSelect((v) => this._selectSize(v)));

      // Size Chart link — open the matching <x-modal> rendered at section level. The button has
      // an `aria-controls` attribute, which theme also wires to its global toggle handler; if the
      // click bubbles up the theme's handler will toggle the modal closed right after we open it
      // (the visible "blink"). stopPropagation prevents that second handler from firing.
      const sizeChartLink = this.querySelector('[data-slot="size-chart-link"]');
      if (sizeChartLink) {
        sizeChartLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const targetId = sizeChartLink.getAttribute('aria-controls');
          if (!targetId) return;
          const modal = document.getElementById(targetId);
          if (!modal) return;
          if (typeof modal.show === 'function') modal.show();
          else if (typeof modal.showModal === 'function') modal.showModal();
          else if (typeof modal.open === 'function') modal.open();
          else modal.setAttribute('open', '');
        });
      }
    }

    _selectGender(g) {
      if (this.state.gender === g) return;
      this.state.gender = g;
      this._ensureValidStyle();
      this._resetSelection();
      this._render();
      persistSession(this.role, this.state);
      notifyState();
    }

    _selectStyle(s) {
      if (this.state.style_tag === s) return;
      this.state.style_tag = s;
      // Capture the label of the newly selected style chip so the preview-title template can interpolate it
      const chip = this.querySelector(`[data-slot="style-chips"] [data-style="${CSS.escape(s)}"]`);
      this.state.style_label = chip ? chip.textContent.trim() : '';
      this._resetSelection();
      this._render();
      persistSession(this.role, this.state);
      notifyState();
    }

    _selectColor(c) {
      if (this.state.color === c) return;
      this.state.color = c;
      // Keep current size if still available under the new color; otherwise clear
      const stillAvailable = this._filtered().some(v => v.color === c && v.size === this.state.size && v.available);
      if (!stillAvailable) {
        this.state.size = null;
      }
      this._resolveVariant();
      this._render();
      persistSession(this.role, this.state);
      notifyState();
    }

    _selectSize(sz) {
      if (this.state.size === sz) return;
      this.state.size = sz;
      this._resolveVariant();
      this._render();
      persistSession(this.role, this.state);
      notifyState();
    }

    _resetSelection() {
      this.state.color = null;
      this.state.size = null;
      // After clearing, auto-select the first available color of the new style/gender filter so
      // the picker is never in an "empty" intermediate state — matches legacy behaviour where
      // switching style preselects the first color tile.
      const firstAvailable = this._filtered().find(v => v.available) || this._filtered()[0];
      if (firstAvailable) {
        this.state.color = firstAvailable.color;
      }
      this._resolveVariant();
    }

    _resolveVariant() {
      // Reset variant_id (only set on full color+size match — gates the ATC enable state).
      this.state.variant_id = null;

      // Pick the best display source with descending priority:
      //   1. exact full match (color + size)  — sets variant_id (ATC-ready)
      //   2. color-only match (any size of selected color)
      //   3. first filtered entry (default style/gender)
      // Legacy behaviour: ATC button shows the running price even before size is chosen, so we
      // always populate price / compare_at_price / image / title / url from whatever source we
      // found — only variant_id distinguishes "ATC-ready" from "estimated display".
      const filtered = this._filtered();
      let source = null;

      if (this.state.color && this.state.size) {
        source = filtered.find(v => v.color === this.state.color && v.size === this.state.size);
        if (source) {
          this.state.variant_id = source.variant_id;
        }
      }
      if (!source && this.state.color) {
        source = filtered.find(v => v.color === this.state.color);
      }
      if (!source) {
        source = filtered[0];
      }

      if (source) {
        this.state.price = source.price;
        this.state.compare_at_price = source.compare_at_price;
        this.state.image = source.image;
        this.state.product_id = source.product_id;
        this.state.product_title = source.product_title;
        this.state.product_url = source.product_url;
      } else {
        this.state.price = null;
        this.state.compare_at_price = null;
        this.state.image = null;
        this.state.product_id = null;
        this.state.product_title = null;
        this.state.product_url = null;
      }
    }

    _matchesFilters(v) {
      if (v.style_tag !== this.state.style_tag) return false;
      if (this.hasGenderToggle && v.sex && v.sex !== this.state.gender) return false;
      return true;
    }

    _filtered() {
      return this.catalog.filter(v => this._matchesFilters(v));
    }

    _styleHasAvailableProducts(styleTag) {
      return this.catalog.some((v) => {
        if (v.style_tag !== styleTag) return false;
        if (!v.available) return false;
        if (this.hasGenderToggle && v.sex && v.sex !== this.state.gender) return false;
        return true;
      });
    }

    _ensureValidStyle() {
      const chips = this.querySelectorAll('[data-slot="style-chips"] [data-style]');
      let firstVisible = null;
      let selectedVisible = false;

      chips.forEach((btn) => {
        const hasProducts = this._styleHasAvailableProducts(btn.dataset.style);
        btn.hidden = !hasProducts;
        if (hasProducts && !firstVisible) firstVisible = btn;
        if (hasProducts && btn.dataset.style === this.state.style_tag) selectedVisible = true;
      });

      const fieldset = this.querySelector('.variant-picker-style');
      if (fieldset) fieldset.hidden = !firstVisible;

      if (!selectedVisible) {
        const next = firstVisible ? firstVisible.dataset.style : null;
        const nextLabel = firstVisible ? firstVisible.textContent.trim() : '';
        if (this.state.style_tag !== next) {
          this.state.style_tag = next;
          this.state.style_label = nextLabel;
          return true;
        }
      }
      return false;
    }

    _render() {
      // Coalesce render calls into a single animation frame so a chain of state mutations
      // (e.g. style click → resetSelection → resolveVariant) only paints once. Reduces the
      // visible flicker that comes from multiple synchronous innerHTML rewrites in the same tick.
      if (this._renderRAF) return;
      this._renderRAF = requestAnimationFrame(() => {
        this._renderRAF = null;
        this._renderGender();
        this._renderStyle();
        this._renderColors();
        this._renderSizes();
        this._renderPreview();
      });
    }

    _renderGender() {
      if (!this.hasGenderToggle) return;
      this.querySelectorAll('[data-slot="gender-toggle"] [data-gender]').forEach(btn => {
        const sel = btn.dataset.gender === this.state.gender;
        btn.classList.toggle('is-selected', sel);
        btn.setAttribute('aria-selected', sel);
      });
    }

    _renderStyle() {
      const changed = this._ensureValidStyle();
      if (changed) {
        this._resetSelection();
        persistSession(this.role, this.state);
        notifyState();
      }
      this.querySelectorAll('[data-slot="style-chips"] [data-style]').forEach(btn => {
        const sel = !btn.hidden && btn.dataset.style === this.state.style_tag;
        btn.classList.toggle('is-selected', sel);
        btn.setAttribute('aria-selected', sel);
      });
    }

    _renderColors() {
      const container = this.querySelector('[data-slot="color-swatches"]');
      if (!container) return;

      // Populate selected colour name next to the COLOR legend (matches theme variant-picker
      // which shows the selected option value next to the option name in the option-info row).
      const colorNameEl = this.querySelector('[data-slot="color-name"]');
      if (colorNameEl) colorNameEl.textContent = this.state.color || '';

      const filtered = this._filtered();
      const byColor = new Map();
      for (const v of filtered) {
        if (!byColor.has(v.color)) {
          byColor.set(v.color, { ...v, anyAvailable: v.available, sizesAvailable: new Set() });
        } else {
          const entry = byColor.get(v.color);
          entry.anyAvailable = entry.anyAvailable || v.available;
        }
        if (v.available) byColor.get(v.color).sizesAvailable.add(v.size);
      }

      // Cross-filter: when a size is selected, mark colors that don't carry that size as soldout
      // (mirrors legacy updateSoldOut where picking size 8 greys out colors without size 8).
      const selectedSize = this.state.size;
      // Match theme variant-picker class names so the bundle reuses main-product PDP styling:
      //   shoe role           → .thumbnail-swatch (rectangular product image, ~83px min-width)
      //   sock/underscrub role → .color-swatch    (small square/circle, --swatch-background CSS var)
      const isShoe = this.role === 'shoe';
      const themeClass = isShoe ? 'thumbnail-swatch' : 'color-swatch';

      const parts = [];
      for (const v of byColor.values()) {
        const selected = v.color === this.state.color;
        const sizeMissing = selectedSize && !v.sizesAvailable.has(selectedSize);
        const soldout = !v.anyAvailable || sizeMissing;
        const bgImage = v.swatch_image || v.image || '';
        const colorSlug = (v.color || '').toLowerCase().replace(/\s+/g, '');
        const stateClasses = `${selected ? ' is-selected' : ''}${soldout ? ' is-disabled' : ''}`;

        if (isShoe) {
          // Theme .thumbnail-swatch expects an <img> child to render
          const imgTag = bgImage
            ? `<img src="${escapeHTML(bgImage)}" alt="${escapeHTML(v.color)}" loading="lazy">`
            : '';
          parts.push(
            `<button type="button"` +
            ` class="bundle-swatch bundle-swatch--color ${themeClass}${stateClasses}"` +
            ` data-color="${escapeHTML(v.color)}"` +
            ` aria-label="${escapeHTML(v.color)}"` +
            ` title="${escapeHTML(v.color)}"` +
            (soldout ? ' disabled' : '') +
            `>${imgTag}</button>`
          );
        } else {
          // Theme .color-swatch reads its visible appearance from --swatch-background
          const swatchBg = bgImage ? `url('${escapeHTML(bgImage)}')` : `linear-gradient(${escapeHTML(colorSlug)}, ${escapeHTML(colorSlug)})`;
          parts.push(
            `<button type="button"` +
            ` class="bundle-swatch bundle-swatch--color ${themeClass}${stateClasses}"` +
            ` data-color="${escapeHTML(v.color)}"` +
            ` aria-label="${escapeHTML(v.color)}"` +
            ` title="${escapeHTML(v.color)}"` +
            ` style="--swatch-background: ${swatchBg};"` +
            (soldout ? ' disabled' : '') +
            `></button>`
          );
        }
      }
      // Skip innerHTML replacement when the rendered string hasn't changed — avoids tearing
      // down identical DOM and triggering image reloads / paint flicker.
      const html = parts.join('');
      if (this._lastColorsHtml !== html) {
        // Capture current visible height BEFORE replacing content so we can animate from it
        // to the new natural height. Avoids the jarring layout jump when the shopper switches
        // between styles with very different color counts (e.g. Strada 19 → Prima 3).
        const oldHeight = container.getBoundingClientRect().height;
        container.style.transition = '';
        container.style.height = `${oldHeight}px`;

        container.innerHTML = html;
        this._lastColorsHtml = html;

        // Force a reflow so the browser registers the explicit pre-transition height
        void container.offsetHeight;
        // Measure the new natural height by temporarily clearing the explicit value
        container.style.height = 'auto';
        const newHeight = container.scrollHeight;
        // Snap back to old height, then transition to new (next frame so browser paints both)
        container.style.height = `${oldHeight}px`;
        requestAnimationFrame(() => {
          container.style.transition = 'height 250ms ease';
          container.style.height = `${newHeight}px`;
        });
        // After the animation completes, release the explicit height so future renders aren't
        // capped by it. Cancel any pending release if a new render starts before this fires.
        if (this._heightReleaseTimer) clearTimeout(this._heightReleaseTimer);
        this._heightReleaseTimer = setTimeout(() => {
          container.style.transition = '';
          container.style.height = '';
        }, 280);
      }
    }

    _renderSizes() {
      const container = this.querySelector('[data-slot="size-swatches"]');
      if (!container) return;

      if (!this.state.color) {
        container.innerHTML = '';
        return;
      }

      const filtered = this._filtered().filter(v => v.color === this.state.color);
      const bySize = new Map();
      for (const v of filtered) {
        if (!bySize.has(v.size)) bySize.set(v.size, v);
      }

      const parts = [];
      for (const v of bySize.values()) {
        const selected = v.size === this.state.size;
        const soldout = !v.available;
        // Add theme .block-swatch class so size pills inherit the same look as main-product PDP
        parts.push(
          `<button type="button"` +
          ` class="bundle-swatch bundle-swatch--size block-swatch${selected ? ' is-selected' : ''}${soldout ? ' is-disabled' : ''}"` +
          ` data-size="${escapeHTML(v.size)}"` +
          (soldout ? ' disabled' : '') +
          `>${escapeHTML(v.size)}</button>`
        );
      }
      const html = parts.join('');
      if (this._lastSizesHtml !== html) {
        container.innerHTML = html;
        this._lastSizesHtml = html;
      }
    }

    _renderPreview() {
      const preview = this.querySelector('[data-slot="preview"]');
      if (!preview) return;

      const imgLink = preview.querySelector('[data-slot="preview-image"]');
      const title = preview.querySelector('[data-slot="preview-title"]');
      const cw = preview.querySelector('[data-slot="preview-colorway"]');
      const yotpo = preview.querySelector('[data-slot="yotpo-widget"]');

      const titleText = this.titleTemplate.replace(/\{style\}/gi, this.state.style_label || '');
      const productUrl = this.state.product_url || '#';

      // Idempotent image render — keep the same <img> element and only swap src/alt when the
      // URL actually changes. Replacing innerHTML on every preview update was forcing the
      // browser to discard and re-decode the image, producing a per-picker flash.
      if (imgLink) {
        if (imgLink.getAttribute('href') !== productUrl) imgLink.setAttribute('href', productUrl);
        let imgEl = imgLink.querySelector('img');
        if (this.state.image) {
          if (!imgEl) {
            imgEl = document.createElement('img');
            imgLink.appendChild(imgEl);
          }
          if (imgEl.getAttribute('src') !== this.state.image) imgEl.src = this.state.image;
          if (imgEl.getAttribute('alt') !== titleText) imgEl.alt = titleText;
        } else if (imgEl) {
          imgEl.remove();
        }
      }
      if (title) {
        if (title.getAttribute('href') !== productUrl) title.setAttribute('href', productUrl);
        if (title.textContent !== titleText) title.textContent = titleText;
      }
      if (cw) {
        const parts = [];
        if (this.state.color) parts.push(this.state.color);
        if (this.state.size) parts.push(`Size ${this.state.size}`);
        const cwText = parts.join(' · ');
        if (cw.textContent !== cwText) cw.textContent = cwText;
      }

      // Yotpo bottom-line widget — set the data attribute then schedule a targeted refresh.
      // scheduleYotpoRefresh re-creates the widget element so Yotpo only re-initializes this
      // one (others on the page stay untouched), and only this picker's loader appears.
      if (yotpo && this.state.product_id != null) {
        const previousId = yotpo.getAttribute('data-yotpo-product-id');
        const newId = String(this.state.product_id);
        if (previousId !== newId) {
          yotpo.setAttribute('data-yotpo-product-id', newId);
          const ratingEl = preview.querySelector('[data-slot="preview-rating"]');
          if (ratingEl) scheduleYotpoRefresh(ratingEl);
        }
      }
    }
  }

  // =========================================================================
  // <bundle-price-summary>
  // =========================================================================
  // Computes:
  //   discountedTotal = totalPrice - (free_picker.price)         where free_picker.role === free_role
  //   compareTotal    = sum of compare_at_price (or price) across pickers
  // The discount mirrors legacy "underscrub free" classic-bundle behaviour. When `data-free-role`
  // is blank, no discount is applied.
  class BundlePriceSummary extends HTMLElement {
    connectedCallback() {
      this.freeRole = this.dataset.freeRole || '';
      this.savingsLabel = this.dataset.savingsLabel || 'You save {amount}';
      this.saleBadgeTemplate = this.dataset.saleBadgeTemplate || 'SALE: {amount} OFF';
      this.showSaleBadge = this.dataset.showSaleBadge === 'true';

      this.saleBadgeEl = this.querySelector('[data-slot="sale-badge"]');
      this.placeholder = this.querySelector('[data-slot="placeholder"]');
      this.values = this.querySelector('[data-slot="values"]');
      this.priceEl = this.querySelector('[data-slot="price"]');
      this.compareEl = this.querySelector('[data-slot="compare"]');

      this._handler = (e) => this._update(e.detail);
      document.addEventListener(EVT_CHANGE, this._handler);
      // Pull current state immediately — pickers may have already fired their initial events
      // before this element registered (custom-element define order is JS-source-order, not DOM order).
      if (registry.size > 0) this._update(buildDetail());
    }
    disconnectedCallback() {
      document.removeEventListener(EVT_CHANGE, this._handler);
    }
    _update(detail) {
      const totals = computeTotals(detail.pickers, this.freeRole);
      const savings = totals.compareTotal - totals.discountedTotal;
      const hasSavings = savings > 0;
      // Treat any non-zero total as "we have prices to show" — auto-color-select on init means
      // every picker reports an estimated price even before the user has interacted, matching
      // legacy behavior where the bundle price is shown immediately on page load.
      const hasPrice = totals.discountedTotal > 0;

      if (this.saleBadgeEl && this.showSaleBadge) {
        this.saleBadgeEl.hidden = !hasSavings;
        if (hasSavings) {
          this.saleBadgeEl.textContent = this.saleBadgeTemplate.replace('{amount}', formatMoney(savings));
        }
      }

      if (!hasPrice) {
        if (this.placeholder) this.placeholder.hidden = false;
        if (this.values) this.values.hidden = true;
        return;
      }
      if (this.placeholder) this.placeholder.hidden = true;
      if (this.values) this.values.hidden = false;

      if (this.priceEl) this.priceEl.textContent = formatMoney(totals.discountedTotal);

      if (this.compareEl) {
        this.compareEl.hidden = !hasSavings;
        if (hasSavings) this.compareEl.textContent = formatMoney(totals.compareTotal);
      }
    }
  }

  // Pure helper — computes bundle totals with optional free-role discount.
  function computeTotals(pickers, freeRole) {
    const total = pickers.reduce((s, p) => s + (p.price || 0), 0);
    const compareTotal = pickers.reduce((s, p) => s + (p.compare_at_price || p.price || 0), 0);
    let discount = 0;
    if (freeRole) {
      const freePicker = pickers.find(p => p.role === freeRole);
      if (freePicker && freePicker.price) discount = freePicker.price;
    }
    return {
      total,
      discount,
      discountedTotal: total - discount,
      compareTotal,
    };
  }

  // =========================================================================
  // <bundle-add-to-cart>
  // =========================================================================
  // Three button states (mirrors legacy bundle.js updateATC):
  //   INITIAL  — no picker has any color selected            → disabled, "Select Bundle"
  //   PARTIAL  — at least one color picked but not every size → disabled, "Select Size" + price
  //   READY    — every picker has a fully resolved variant id → enabled, "Add to Cart" + price
  //
  // When price is shown inline, format mirrors legacy: "{label}  {discounted}  {compareStrikethrough}"
  // Cart submission attaches `_bundle: true` line item property so a Shopify Function or back-end
  // script can recognise the line as a bundle and apply server-side discount logic.
  // Module-scope guard — the bundle ATC renders twice (inline + mobile-sticky duplicate),
  // and we don't want a fast double-click across both copies to POST /cart/add.js twice.
  let bundleAtcSubmitting = false;

  class BundleAddToCart extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button, a');
      this.disabledLabel = this.dataset.disabledLabel || 'Select Bundle';
      this.partialLabel = this.dataset.partialLabel || 'Select Size';
      this.readyLabel = this.dataset.readyLabel || 'Add to Cart';
      this.showInlinePrice = this.dataset.showInlinePrice !== 'false';
      this.variant = this.dataset.variant || 'inline';

      // Discover the page-level free_role from the price summary element if present.
      const summary = document.querySelector('bundle-price-summary');
      this.freeRole = summary ? (summary.dataset.freeRole || '') : '';

      this._handler = (e) => this._sync(e.detail);
      document.addEventListener(EVT_CHANGE, this._handler);

      if (this.button) {
        this.button.addEventListener('click', (e) => this._submit(e));
      }
      // Pull current state immediately in case pickers fired before this element registered
      if (registry.size > 0) this._sync(buildDetail());

      // Mobile-sticky duplicate: hide whenever the inline copy is on-screen so the shopper
      // sees only one button. Mirrors the legacy `#product-sticky-b` slideUp/slideDown logic
      // but driven by IntersectionObserver instead of scroll math.
      if (this.variant === 'inline' && 'IntersectionObserver' in window) {
        // Defer so the sticky duplicate has time to mount on its own connectedCallback.
        requestAnimationFrame(() => {
          const stickyCopies = document.querySelectorAll('.bundle-add-to-cart--mobile-sticky');
          if (stickyCopies.length === 0) return;
          // Initial state: assume inline NOT in viewport — sticky shows. The observer fires
          // once on attach with the current intersection ratio so it'll correct immediately.
          stickyCopies.forEach(el => el.classList.remove('is-hidden'));
          this._stickyObserver = new IntersectionObserver((entries) => {
            const inlineVisible = entries[0]?.isIntersecting;
            stickyCopies.forEach(el => el.classList.toggle('is-hidden', !!inlineVisible));
          }, { threshold: 0.01 });
          this._stickyObserver.observe(this);
        });
      }
    }
    disconnectedCallback() {
      document.removeEventListener(EVT_CHANGE, this._handler);
      if (this._stickyObserver) this._stickyObserver.disconnect();
    }
    _sync(detail) {
      if (!this.button) return;

      let label;
      let isReady = false;
      if (detail.allReady) {
        label = this.readyLabel;
        isReady = true;
      } else if (detail.anySelected) {
        label = this.partialLabel;
      } else {
        label = this.disabledLabel;
      }

      this.button.disabled = !isReady;
      this.button.setAttribute('aria-disabled', !isReady);
      this.button.classList.toggle('is-ready', isReady);

      const totals = computeTotals(detail.pickers, this.freeRole);
      // Show inline price as soon as we have ANY computed total — auto-color-select on init
      // means the estimated price is available right at page load, so the button reads
      // e.g. "Select Size · $193 $241" from the very first paint.
      const showPrice = this.showInlinePrice && totals.discountedTotal > 0;

      if (showPrice) {
        const hasSavings = totals.compareTotal > totals.discountedTotal;
        const sep = '<span class="bundle-add-to-cart__sep" aria-hidden="true"></span>';
        const priceHtml = `<span class="bundle-add-to-cart__price">${escapeHTML(formatMoney(totals.discountedTotal))}</span>`;
        const compareHtml = hasSavings
          ? `<span class="bundle-add-to-cart__compare">${escapeHTML(formatMoney(totals.compareTotal))}</span>`
          : '';
        this.button.innerHTML =
          `<span class="bundle-add-to-cart__label">${escapeHTML(label)}</span>${sep}${priceHtml}${compareHtml}`;
      } else {
        this.button.textContent = label;
      }
    }
    async _submit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled) return;
      // Guard against the user tapping both the inline and the mobile-sticky duplicate in
      // quick succession — without this, the cart would receive two POSTs.
      if (bundleAtcSubmitting) return;

      const items = Array.from(registry.values())
        .map(v => v.state.variant_id)
        .filter(Boolean)
        .map(id => ({ id, quantity: 1, properties: { _bundle: true } }));

      if (items.length === 0) return;

      bundleAtcSubmitting = true;
      this.button.disabled = true;
      const previousHtml = this.button.innerHTML;
      this.button.textContent = 'Adding…';

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.description || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        document.dispatchEvent(new CustomEvent(EVT_ADDED, { detail: data, bubbles: true }));

        // Open theme cart drawer. The target theme's <cart-drawer> extends Drawer with a
        // show() method, and listens for `cart:refresh` to re-fetch its section content.
        // Pattern from theme.js (cart-drawer.js): dispatch refresh first, then call show().
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof drawer.show === 'function') {
            drawer.show();
          } else if (typeof drawer.open === 'function') {
            drawer.open();
          }
          this.button.innerHTML = previousHtml;
          this.button.disabled = false;
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        console.error('[bundle-add-to-cart] add failed:', err);
        this.button.disabled = false;
        this.button.innerHTML = previousHtml;
        alert(`Couldn't add this bundle to your cart: ${err.message || 'please try again'}`);
      } finally {
        bundleAtcSubmitting = false;
      }
    }
  }

  // =========================================================================
  // <bundle-sale-badge>
  // =========================================================================
  // Standalone pill badge (e.g. "SALE: $48 OFF") that can be positioned anywhere on the page.
  // Sources the free_role from any sibling <bundle-price-summary> on the same page so the
  // discount calculation stays consistent. Always visible after JS init when savings > 0.
  class BundleSaleBadge extends HTMLElement {
    connectedCallback() {
      this.template = this.dataset.template || 'SALE: {amount} OFF';
      this.textEl = this.querySelector('[data-slot="text"]');

      const summary = document.querySelector('bundle-price-summary');
      this.freeRole = summary ? (summary.dataset.freeRole || '') : '';

      // Hide initially until first picker-change event fires (JS init takes microseconds, so
      // the badge appears effectively immediately on page load with the correct discount).
      this.style.display = 'none';

      this._handler = (e) => this._update(e.detail);
      document.addEventListener(EVT_CHANGE, this._handler);
      // Pull current state immediately in case pickers fired before this element registered
      if (registry.size > 0) this._update(buildDetail());
    }
    disconnectedCallback() {
      document.removeEventListener(EVT_CHANGE, this._handler);
    }
    _update(detail) {
      const totals = computeTotals(detail.pickers, this.freeRole);
      const savings = totals.compareTotal - totals.discountedTotal;
      if (savings > 0) {
        this.style.display = '';
        if (this.textEl) {
          this.textEl.textContent = this.template.replace('{amount}', formatMoney(savings));
        }
      } else {
        this.style.display = 'none';
      }
    }
  }

  // =========================================================================
  // <bundle-mobile-summary>
  // =========================================================================
  // Mobile-only one-row summary directly under the bundle product title:
  //   "SNEAKER + UNDERSCRUB + SOCK   $178  $226̶"
  // Listens to bundle:picker-change and updates the live price + compare-at strikethrough.
  class BundleMobileSummary extends HTMLElement {
    connectedCallback() {
      this.freeRole = this.dataset.freeRole || '';
      this.priceEl = this.querySelector('[data-slot="price"]');
      this.compareEl = this.querySelector('[data-slot="compare"]');

      this._handler = (e) => this._update(e.detail);
      document.addEventListener(EVT_CHANGE, this._handler);
      if (registry.size > 0) this._update(buildDetail());
    }
    disconnectedCallback() {
      document.removeEventListener(EVT_CHANGE, this._handler);
    }
    _update(detail) {
      const totals = computeTotals(detail.pickers, this.freeRole);
      const hasPrice = totals.discountedTotal > 0;
      const hasSavings = totals.compareTotal > totals.discountedTotal;

      if (this.priceEl) {
        this.priceEl.textContent = hasPrice ? formatMoney(totals.discountedTotal) : '';
      }
      if (this.compareEl) {
        this.compareEl.hidden = !hasSavings;
        if (hasSavings) this.compareEl.textContent = formatMoney(totals.compareTotal);
      }
    }
  }

  if (!customElements.get('bundle-item-picker')) customElements.define('bundle-item-picker', BundleItemPicker);
  if (!customElements.get('bundle-price-summary')) customElements.define('bundle-price-summary', BundlePriceSummary);
  if (!customElements.get('bundle-add-to-cart')) customElements.define('bundle-add-to-cart', BundleAddToCart);
  if (!customElements.get('bundle-sale-badge')) customElements.define('bundle-sale-badge', BundleSaleBadge);
  if (!customElements.get('bundle-mobile-summary')) customElements.define('bundle-mobile-summary', BundleMobileSummary);

  // ---------------------------------------------------------------------------
  // Size-chart table wrapping
  // ---------------------------------------------------------------------------
  // Each `<table>` inside `.bundle-size-chart-modal` gets wrapped in a div with
  // `overflow-x: auto`, so the table can scroll horizontally while the modal
  // body itself never does. Native <table> layout is preserved (so thead/tbody
  // columns auto-align), the wrapper just owns the scroll.
  function wrapBundleSizeChartTables() {
    document.querySelectorAll('.bundle-size-chart-modal table').forEach(table => {
      if (table.parentElement && table.parentElement.classList.contains('bundle-size-chart-table-scroll')) return;
      const wrap = document.createElement('div');
      wrap.className = 'bundle-size-chart-table-scroll';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wrapBundleSizeChartTables);
  } else {
    wrapBundleSizeChartTables();
  }
})();
